// The one-tab sign-in lock (PB-5461, d21). Several tabs that need a sign-in at
// the same moment — a first visit that opens three at once — used to go to
// Keycloak together, and Keycloak then refused the first tab's password once.
// Now ONE tab, the leader, goes; the others wait for its session.
//
//   localStorage    pb.auth.signInLease  {tabId, flowState, at, expiresAt}
//                                        who is signing in, and in which flow
//   sessionStorage  pb.auth.tabId        <random>  this tab's id
//
// The lease is read by every tab of the app, so it lives in localStorage. The
// tab id belongs to one tab and has to survive its round trip through Keycloak,
// so it lives in sessionStorage. Neither is a credential.
//
// Who may treat a live lease as its own (PR 407 review round 2 F1). Not every
// page with the lease's tab id: a copied tab carries the same id, and its page
// can load while the owner is away at Keycloak, when no live page answers for
// that id. So a live lease belongs only to
//   - the page load that wrote it — by winning an election, or by a sign-in the
//     person started there — which remembers it in memory, so neither a copy
//     nor a later page of the same tab inherits it; and
//   - the callback of the flow it names. Once the leader has its Keycloak
//     address, the lease records that flow's OIDC `state`, and only a return to
//     `/auth/callback` with that state is the genuine end of the flow.
// Every other page that finds a live lease follows it, a later page of the
// owner's own tab included: it cannot tell itself from a copy. A callback of
// another flow is no different, even with this tab's id: `/auth/callback` is a
// public route, so a copy can open one too (PR 407 review 4). Back after a
// sign-out to an older Keycloak page therefore waits as well. It waits until
// the lease ends, or until the person starts a sign-in from it.
//
// A tab opened by another (`window.open`) or with "Duplicate tab" starts with a
// COPY of that tab's sessionStorage, tab id included (PR 407 review F1). So the
// first time a page load uses a kept id, it asks the app's other live pages on
// the `pb-auth-tabs` channel whether one of them holds that id; when one says
// "mine", this page is the copy and makes a new id. A tab back from Keycloak
// keeps its id: the page that left is gone, so nobody answers for it. This
// keeps the ids of live pages apart; who owns the lease no longer rests on it.
//
// Every access is wrapped. When either store, or the lock that makes the
// election atomic, is missing, sign-in works as it did before this lock — each
// tab signs itself in — rather than waiting on a lease nobody can hold. Without
// BroadcastChannel a kept id is used unasked, as before the check.

const LEASE_KEY = "pb.auth.signInLease";
const TAB_ID_KEY = "pb.auth.tabId";

/** The channel the app's live pages ask each other on: "who has this tab id?" */
export const TAB_ID_CHANNEL = "pb-auth-tabs";

/** How long a page waits for another live page to say a kept tab id is its own. */
export const TAB_ID_CHECK_MS = 150;

/** A lease ends two minutes after it was taken, so a tab that never comes back
 *  from Keycloak holds the others back for that long at most. */
export const SIGN_IN_LEASE_TTL_MS = 2 * 60_000;

/** The Web Lock every tab takes around its read-modify-write of the lease. */
export const SIGN_IN_ELECTION_LOCK = "pb-sign-in-election";

/** The channel a tab announces its finished sign-in on. */
export const AUTH_CHANNEL = "pb-auth";
export const SIGNED_IN_MESSAGE = "signed-in";

/** Without Web Locks: how long a tab waits before it reads its claim back.
 *  The jitter keeps two tabs that wrote together from reading together. */
export const FALLBACK_SETTLE_MS = 100;
const FALLBACK_JITTER_MS = 50;

interface SignInLease {
  readonly tabId: string;
  /** The OIDC `state` of the flow this sign-in is in, once its Keycloak
   *  address is known; `null` until then. */
  readonly flowState: string | null;
  readonly at: number;
  readonly expiresAt: number;
}

/** One page load's hold on its tab id: the tab's sessionStorage, the channel it
 *  answers the other pages on, and the check of the id it found there. */
interface ThisPage {
  readonly storage: Storage | null;
  readonly channel: BroadcastChannel | null;
  readonly checked: Promise<void>;
}

type TabIdMessage =
  | { readonly kind: "who-has"; readonly tabId: string; readonly from: string }
  | { readonly kind: "mine"; readonly to: string };

let thisPage: ThisPage | null = null;
let tabIdCheckMs = TAB_ID_CHECK_MS;
/** The lease this page load last wrote: the only one it owns by writing. */
let leaseWrittenHere: SignInLease | null = null;

/** This tab's id, made on first use. `null` when it cannot be kept: a tab that
 *  cannot recognise its own lease must not take part in the lock. The first
 *  call in a page load waits until no other live page holds the id. */
async function thisTabId(): Promise<string | null> {
  thisPage ??= loadThisPage();
  await thisPage.checked;
  const { storage } = thisPage;
  if (storage === null) return null;
  try {
    return storage.getItem(TAB_ID_KEY) || renewTabId(storage);
  } catch {
    return null;
  }
}

function renewTabId(storage: Storage): string {
  const made = newTabId();
  storage.setItem(TAB_ID_KEY, made);
  return made;
}

/**
 * Start answering the other pages for this one, and ask them about the id this
 * tab kept. A copied tab finds its id claimed by the live tab it was copied
 * from and replaces it; with no kept id there is nothing to ask, since a new id
 * is nobody else's.
 */
function loadThisPage(): ThisPage {
  let storage: Storage;
  let kept: string | null;
  try {
    storage = window.sessionStorage;
    kept = storage.getItem(TAB_ID_KEY);
  } catch {
    return { storage: null, channel: null, checked: Promise.resolve() };
  }
  let channel: BroadcastChannel;
  try {
    channel = new BroadcastChannel(TAB_ID_CHANNEL);
  } catch {
    return { storage, channel: null, checked: Promise.resolve() };
  }
  // Names this page load only; never stored, so a copy cannot share it.
  const instance = newTabId();
  let onMine: (() => void) | null = null;
  channel.addEventListener("message", (event: MessageEvent) => {
    const message = readTabIdMessage(event.data);
    if (message?.kind === "mine") {
      if (message.to === instance) onMine?.();
    } else if (
      message?.kind === "who-has" &&
      message.from !== instance &&
      message.tabId === storedTabId(storage)
    ) {
      channel.postMessage({ kind: "mine", to: message.from });
    }
  });
  if (!kept) return { storage, channel, checked: Promise.resolve() };

  const checked = new Promise<void>((resolve) => {
    const timer = setTimeout(() => {
      onMine = null;
      resolve();
    }, tabIdCheckMs);
    onMine = () => {
      onMine = null;
      clearTimeout(timer);
      try {
        renewTabId(storage);
      } catch {
        // The read after the check finds no id it can keep, and answers null.
      }
      resolve();
    };
    channel.postMessage({ kind: "who-has", tabId: kept, from: instance });
  });
  return { storage, channel, checked };
}

function storedTabId(storage: Storage): string | null {
  try {
    return storage.getItem(TAB_ID_KEY);
  } catch {
    return null;
  }
}

/** Another page's message, or `null` for anything this code did not send. */
function readTabIdMessage(data: unknown): TabIdMessage | null {
  if (typeof data !== "object" || data === null) return null;
  const { kind, tabId, from, to } = data as Record<string, unknown>;
  if (
    kind === "who-has" &&
    typeof tabId === "string" &&
    typeof from === "string"
  )
    return { kind, tabId, from };
  if (kind === "mine" && typeof to === "string") return { kind, to };
  return null;
}

/**
 * End this page load: its hold on its tab id, closing its channel, and its
 * memory of the lease it wrote, so the next call starts a new page load. Module
 * state outlives a test, so one spec's page would otherwise answer for the next
 * spec's tab, or own its lease. `checkMs` sets how long the next check waits
 * for an answer. Exported for tests only; nothing in the app calls it.
 */
export function __resetSignInTabForTests(
  checkMs: number = TAB_ID_CHECK_MS,
): void {
  thisPage?.channel?.close();
  thisPage = null;
  leaseWrittenHere = null;
  tabIdCheckMs = checkMs;
}

/** Not a secret, only a name: `randomUUID` needs a secure context, and an
 *  address served over plain http still gets a working id. */
function newTabId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/** Reads the stored lease. Throws when storage does; `null` for none, or for
 *  one this code did not write. */
function readLease(): SignInLease | null {
  const raw = window.localStorage.getItem(LEASE_KEY);
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const { tabId, flowState, at, expiresAt } = parsed as Record<
      string,
      unknown
    >;
    return typeof tabId === "string" &&
      tabId.length > 0 &&
      typeof at === "number" &&
      Number.isFinite(at) &&
      typeof expiresAt === "number" &&
      Number.isFinite(expiresAt)
      ? {
          tabId,
          // A lease from before flows were recorded names none.
          flowState:
            typeof flowState === "string" && flowState.length > 0
              ? flowState
              : null,
          at,
          expiresAt,
        }
      : null;
  } catch {
    return null;
  }
}

/** Store `lease` as this page load's own. Throws when storage does. */
function writeLease(lease: SignInLease): void {
  window.localStorage.setItem(LEASE_KEY, JSON.stringify(lease));
  leaseWrittenHere = lease;
}

/** A new lease for this tab, its flow not yet known. */
function freshLease(tabId: string, now: number): SignInLease {
  return {
    tabId,
    flowState: null,
    at: now,
    expiresAt: now + SIGN_IN_LEASE_TTL_MS,
  };
}

/** True for the lease this page load wrote, and for no lease a copy, an
 *  earlier page of this tab, or another tab wrote. */
function writtenHere(lease: SignInLease | null): boolean {
  return (
    lease !== null &&
    leaseWrittenHere !== null &&
    lease.tabId === leaseWrittenHere.tabId &&
    lease.at === leaseWrittenHere.at
  );
}

/** True when `lease` names the flow `flowState`, as the callback that flow
 *  returns to knows it. */
function namesFlow(lease: SignInLease, flowState: string | null): boolean {
  return flowState !== null && lease.flowState === flowState;
}

/** A lease ending further away than one lifetime was not written by this code,
 *  and must not hold every tab back. */
function isLive(lease: SignInLease, now: number): boolean {
  return now < lease.expiresAt && lease.expiresAt - now <= SIGN_IN_LEASE_TTL_MS;
}

/** Take the lease when it is free or ended, already this page load's, or
 *  names the flow `flowState` this callback returned from. Throws when storage
 *  does. */
function claimIfFree(
  tabId: string,
  now: number,
  flowState: string | null,
): boolean {
  const lease = readLease();
  if (
    lease !== null &&
    isLive(lease, now) &&
    !writtenHere(lease) &&
    !namesFlow(lease, flowState)
  ) {
    return false;
  }
  writeLease(freshLease(tabId, now));
  return true;
}

function webLocks(): LockManager | null {
  return typeof navigator !== "undefined" && navigator.locks
    ? navigator.locks
    : null;
}

function settle(): Promise<void> {
  const wait = FALLBACK_SETTLE_MS + Math.random() * FALLBACK_JITTER_MS;
  return new Promise((resolve) => setTimeout(resolve, wait));
}

/**
 * Decide whether THIS page starts the sign-in it needs by itself. True when it
 * leads — the lease was free, ended or this page load's own, and is now its
 * own — and also whenever the lock cannot work here (no storage, no tab id, a
 * failing lock): then every tab signs itself in, as before. A live lease of any
 * other page makes this one follow, a page of this same tab included.
 *
 * `flowState` is for `/auth/callback`: the OIDC `state` it returned with. A
 * live lease naming that flow is this callback's own, to restart in.
 *
 * Atomic across tabs with Web Locks: the read and the write happen inside one
 * lock, so two tabs can never both find the lease free. Without Web Locks the
 * tab writes its claim, waits {@link FALLBACK_SETTLE_MS} plus a little jitter
 * and reads it back; it leads only if the claim is still its own.
 */
export async function electSignInLeader(
  options: { readonly flowState?: string | null } = {},
): Promise<boolean> {
  const flowState = options.flowState ?? null;
  const tabId = await thisTabId();
  if (tabId === null) return true;
  try {
    const locks = webLocks();
    if (locks) {
      return await locks.request(SIGN_IN_ELECTION_LOCK, () =>
        claimIfFree(tabId, Date.now(), flowState),
      );
    }
    if (!claimIfFree(tabId, Date.now(), flowState)) return false;
    await settle();
    return writtenHere(readLease());
  } catch {
    return true;
  }
}

/**
 * Take the lease, whoever holds it. For the sign-ins a person starts in this
 * tab — Try again, Choose another account, Sign in here instead, `/auth/restart`
 * — which are a choice of tab, not something to elect.
 */
export async function takeSignInLease(now: number = Date.now()): Promise<void> {
  const tabId = await thisTabId();
  if (tabId === null) return;
  try {
    writeLease(freshLease(tabId, now));
  } catch {
    // No lease can be read either, so no tab waits on this one.
  }
}

/**
 * Name the flow this page's lease is for, once its sign-in start has the
 * Keycloak address: that address's OIDC `state`. Only the callback returning
 * with the same state may then give the lease up, or restart in it. A lease
 * this page load did not write — another tab took over meanwhile — is left
 * alone, and so is everything when the address carries no state.
 */
export function recordSignInFlow(authorizationUrl: string): void {
  let flowState: string | null;
  try {
    flowState =
      new URL(authorizationUrl, window.location.href).searchParams.get(
        "state",
      ) || null;
  } catch {
    flowState = null;
  }
  if (flowState === null) return;
  try {
    const lease = readLease();
    if (lease !== null && writtenHere(lease))
      writeLease({ ...lease, flowState });
  } catch {
    // No lease can be read either, so no tab waits on this one.
  }
}

/** True while a live lease is not this page load's own: another tab, or an
 *  earlier page of this one, is signing in. */
export function signInLeaseHeldElsewhere(now: number = Date.now()): boolean {
  try {
    const lease = readLease();
    return lease !== null && isLive(lease, now) && !writtenHere(lease);
  } catch {
    return false;
  }
}

/**
 * Give the lease up once a sign-in has succeeded, when it is this page's: the
 * lease this page load wrote, or the one naming `flowState`, the flow this
 * callback returned from. A lease that has ended, or that this code cannot
 * read, goes too. Any other live lease stays: its sign-in is still under way,
 * and a page that did not start it has no say over it.
 */
export function releaseSignInLease(
  flowState: string | null = null,
  now: number = Date.now(),
): void {
  try {
    if (window.localStorage.getItem(LEASE_KEY) === null) return;
    const lease = readLease();
    if (
      lease === null ||
      !isLive(lease, now) ||
      writtenHere(lease) ||
      namesFlow(lease, flowState)
    ) {
      window.localStorage.removeItem(LEASE_KEY);
    }
  } catch {
    // Nothing stored can be read back either.
  }
}

/** Remove the lease, whoever holds it, as sign-out does. */
export function clearSignInLease(): void {
  try {
    window.localStorage.removeItem(LEASE_KEY);
  } catch {
    // Nothing stored can be read back either.
  }
}

/** Tell the waiting tabs this browser now has a session. */
export function announceSignedIn(): void {
  try {
    const channel = new BroadcastChannel(AUTH_CHANNEL);
    channel.postMessage(SIGNED_IN_MESSAGE);
    channel.close();
  } catch {
    // The lease's own storage event, and the waiting tabs' polling, still tell them.
  }
}

/**
 * Call `listener` whenever another tab may have finished signing in: the lease
 * changed (the `storage` event, which only other tabs see), a "signed-in"
 * announcement arrived, or this window got focus back. Returns the unsubscribe.
 */
export function onSignInNews(listener: () => void): () => void {
  const onStorage = (event: StorageEvent): void => {
    // `null` is a `clear()` of the whole store, the lease with it.
    if (event.key === LEASE_KEY || event.key === null) listener();
  };
  const onMessage = (event: MessageEvent): void => {
    if (event.data === SIGNED_IN_MESSAGE) listener();
  };
  const onFocus = (): void => listener();
  window.addEventListener("storage", onStorage);
  window.addEventListener("focus", onFocus);
  let channel: BroadcastChannel | null = null;
  try {
    channel = new BroadcastChannel(AUTH_CHANNEL);
    channel.addEventListener("message", onMessage);
  } catch {
    channel = null;
  }
  return () => {
    window.removeEventListener("storage", onStorage);
    window.removeEventListener("focus", onFocus);
    channel?.close();
  };
}
